import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import * as taskboardService from '../services/taskboardService';

interface SprintCompletionModalProps {
  sprint: any;
  incompleteItems: any[];
  availableSprints: any[];
  onClose: () => void;
  onComplete: () => void;
}

export function SprintCompletionModal({ sprint, incompleteItems, availableSprints, onClose, onComplete }: SprintCompletionModalProps): React.ReactElement {
  const { token } = useAuth();
  const [actions, setActions] = useState<Record<string, 'backlog' | 'next_sprint'>>(
    Object.fromEntries(incompleteItems.map(item => [item.id, 'backlog']))
  );
  const [sprintDestination, setSprintDestination] = useState<'existing' | 'create'>(
    availableSprints.length > 0 ? 'existing' : 'create'
  );
  const [selectedSprintId, setSelectedSprintId] = useState<string>(
    availableSprints.length > 0 ? availableSprints[0].id : ''
  );
  const [createNextSprint, setCreateNextSprint] = useState(false);
  const [nextSprintName, setNextSprintName] = useState(`Sprint ${(sprint.sprint_number || 0) + 1}`);
  const [nextSprintGoal, setNextSprintGoal] = useState('');
  const [nextSprintStart, setNextSprintStart] = useState('');
  const [nextSprintEnd, setNextSprintEnd] = useState('');
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');

  // Check if any item is set to next_sprint
  const hasNextSprintItems = Object.values(actions).some(a => a === 'next_sprint');

  // Show the sprint destination section when items are assigned to next sprint
  const showSprintSection = hasNextSprintItems || createNextSprint;

  async function handleComplete() {
    if (!token) return;

    // If items want next_sprint but no destination chosen yet, show the section
    if (hasNextSprintItems && !showSprintSection) {
      setCreateNextSprint(true);
      return;
    }

    // Validate destination if items are going to next sprint
    if (hasNextSprintItems) {
      if (sprintDestination === 'create' && (!nextSprintName || !nextSprintStart || !nextSprintEnd)) {
        setError('Please fill in all new sprint fields');
        return;
      }
      if (sprintDestination === 'existing' && !selectedSprintId) {
        setError('Please select a sprint');
        return;
      }
    }

    setCompleting(true);
    setError('');

    try {
      const completionData: taskboardService.CompletionData = {
        actions: Object.entries(actions).map(([id, action]) => ({
          requirement_id: id,
          action,
        })),
      };

      if (hasNextSprintItems) {
        if (sprintDestination === 'existing') {
          completionData.existing_sprint_id = selectedSprintId;
        } else {
          completionData.create_next_sprint = {
            name: nextSprintName,
            goal: nextSprintGoal || undefined,
            start_date: nextSprintStart,
            end_date: nextSprintEnd,
          };
        }
      }

      await taskboardService.completeSprint(sprint.id, completionData, token);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete sprint');
      setCompleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Complete Sprint: {sprint.name}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {incompleteItems.length} incomplete item{incompleteItems.length !== 1 ? 's' : ''} remaining
          </p>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400 p-3 rounded-lg">{error}</div>
          )}

          {/* Incomplete items */}
          {incompleteItems.length > 0 ? (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">What should happen to these items?</h3>
              <div className="space-y-2">
                {incompleteItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{item.story_type || 'No type'} - {item.priority}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => setActions({ ...actions, [item.id]: 'backlog' })}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          actions[item.id] === 'backlog'
                            ? 'bg-gray-700 text-white dark:bg-gray-300 dark:text-gray-900'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300'
                        }`}
                      >
                        Backlog
                      </button>
                      <button
                        onClick={() => setActions({ ...actions, [item.id]: 'next_sprint' })}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          actions[item.id] === 'next_sprint'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-300'
                        }`}
                      >
                        Next Sprint
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">All items are completed. Ready to close the sprint.</p>
          )}

          {/* Sprint destination section */}
          {showSprintSection && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Move items to:</h3>

              {/* Toggle between existing and create */}
              <div className="flex gap-2 mb-4">
                {availableSprints.length > 0 && (
                  <button
                    onClick={() => setSprintDestination('existing')}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      sprintDestination === 'existing'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Existing Sprint
                  </button>
                )}
                <button
                  onClick={() => setSprintDestination('create')}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    sprintDestination === 'create'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Create New Sprint
                </button>
              </div>

              {/* Existing sprint selector */}
              {sprintDestination === 'existing' && availableSprints.length > 0 && (
                <div className="space-y-2">
                  {availableSprints.map(s => (
                    <label
                      key={s.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedSprintId === s.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-400'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="targetSprint"
                        value={s.id}
                        checked={selectedSprintId === s.id}
                        onChange={() => setSelectedSprintId(s.id)}
                        className="text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            s.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            s.status === 'planning' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {s.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {new Date(s.start_date).toLocaleDateString()} - {new Date(s.end_date).toLocaleDateString()}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Create new sprint form */}
              {sprintDestination === 'create' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={nextSprintName}
                      onChange={e => setNextSprintName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="Sprint name"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={nextSprintGoal}
                      onChange={e => setNextSprintGoal(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      placeholder="Sprint goal (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={nextSprintStart}
                      onChange={e => setNextSprintStart(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End Date</label>
                    <input
                      type="date"
                      value={nextSprintEnd}
                      onChange={e => setNextSprintEnd(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={completing}
            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-full hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {completing ? 'Completing...' : 'Complete Sprint'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SprintCompletionModal;
